<template>
    <div class="min-h-screen bg-gray-100 flex flex-col" @contextmenu="preventContextMenu">
        <!-- Vendor-restricted (DRM) audiobook player: streamed from a session-gated
             endpoint (HTTP Range), not downloaded. controlsList "nodownload" hides
             the browser's download affordance; same-origin requests carry the
             session cookie automatically. -->
        <div class="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary text-white">
            <a href="/my-library" class="font-marketing font-semibold inline-flex items-center gap-1 shrink-0 hover:text-green-200">
                ← Library
            </a>
            <span class="text-sm font-sans opacity-80 truncate">{{ meta.title || (mode === 'sample' ? 'Audiobook sample' : 'Now playing') }}</span>
            <span class="shrink-0 w-8"></span>
        </div>

        <div class="flex-1 flex items-center justify-center p-6">
            <div class="w-full max-w-xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <div class="flex items-center gap-4 mb-5">
                    <img v-if="meta.cover" :src="meta.cover" :alt="meta.title"
                        class="h-24 w-16 object-cover rounded shadow shrink-0" />
                    <div class="min-w-0">
                        <h1 class="text-xl font-marketing font-semibold text-primary leading-tight truncate" :title="meta.title">
                            {{ meta.title || (mode === 'sample' ? 'Audiobook sample' : 'Now playing') }}
                        </h1>
                        <p class="text-sm text-gray-600 font-sans truncate">
                            {{ mode === 'sample' ? 'Free sample' : (meta.author || 'Audiobook') }}
                        </p>
                    </div>
                </div>
                <audio ref="player" :src="src" controls controlsList="nodownload noplaybackrate"
                    crossorigin="use-credentials" class="w-full" preload="metadata"
                    @loadedmetadata="onLoaded" @timeupdate="onTimeUpdate" @pause="saveNow" @error="onError">
                    Your browser does not support the audio element.
                </audio>
                <p v-if="error" class="mt-4 text-sm text-red-600 font-sans">{{ error }}</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { userService } from '../services/user'

const props = defineProps({
    id: { type: [String, Number], default: null },
    mode: { type: String, default: 'purchase' } // 'purchase' | 'sample'
})

const player = ref(null)
const error = ref('')
const meta = ref({ title: '', author: '', cover: '' })

const loadMeta = async () => {
    const m = props.mode === 'sample'
        ? await userService.getSampleMeta(props.id)
        : await userService.getPurchaseMeta(props.id)
    if (m) meta.value = { ...meta.value, ...m }
}
onMounted(loadMeta)

const src = computed(() =>
    props.mode === 'sample'
        ? userService.sampleContentUrl(props.id)
        : userService.streamUrl(props.id)
)

const onError = () => {
    error.value = 'Could not play this audiobook. Please try again.'
}

const preventContextMenu = (e) => e.preventDefault()

// --- Resume progress (purchased items only; samples don't track) ---
const isResumable = () => props.mode !== 'sample' && !!props.id
let lastSave = 0

const onLoaded = async () => {
    if (!isResumable() || !player.value) return
    const saved = await userService.getProgress(props.id)
    const secs = parseFloat(saved)
    if (saved != null && !Number.isNaN(secs) && secs > 0) {
        try { player.value.currentTime = secs } catch (e) { /* not seekable yet */ }
    }
}

const saveNow = () => {
    if (isResumable() && player.value && player.value.currentTime > 0) {
        userService.saveProgress(props.id, player.value.currentTime)
    }
}

const onTimeUpdate = () => {
    const now = Date.now()
    if (now - lastSave > 5000) { lastSave = now; saveNow() }
}

onBeforeUnmount(saveNow)
</script>
